
import React, { useEffect, useRef, useState } from "react";
import { Skeleton, Stack } from "@mantine/core";

interface TikTokEmbedProps {
  videoUrl: string; // Full TikTok video URL, e.g., "https://www.tiktok.com/@scout2015/video/6718335390845095173"
}

function TikTokEmbed({ videoUrl }: TikTokEmbedProps) {
  const embedRef = useRef<HTMLDivElement>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!embedRef.current) return;

    setIsLoading(true);

    // Extract video ID from the URL
    const videoIdMatch = videoUrl.match(/\/video\/(\d+)/);
    const videoId = videoIdMatch ? videoIdMatch[1] : null;

    if (!videoId) {
      console.error("Invalid TikTok video URL provided:", videoUrl);
      setIsLoading(false);
      return;
    }

    // Clear any existing embed content
    embedRef.current.innerHTML = "";

    // Create the blockquote element
    const blockquote = document.createElement("blockquote");
    blockquote.className = "tiktok-embed";
    blockquote.cite = videoUrl;
    blockquote.setAttribute("data-video-id", videoId);
    blockquote.style.maxWidth = "605px";
    blockquote.style.minWidth = "325px";
    blockquote.style.margin = "0px auto"; // Center the embed

    const section = document.createElement("section");
    blockquote.appendChild(section);
    embedRef.current.appendChild(blockquote);

    // Set a timeout to hide loading state after a reasonable time
    const loadingTimeout = setTimeout(() => {
      setIsLoading(false);
    }, 3000);

    // Load the TikTok embed script if not already loaded
    if (!document.querySelector("script[src=\"https://www.tiktok.com/embed.js\"]")) {
      const script = document.createElement("script");
      script.src = "https://www.tiktok.com/embed.js";
      script.async = true;
      script.onload = () => {
        setIsLoading(false);
      };
      document.body.appendChild(script);
    } else {
        // @ts-ignore
        window.tiktok?.widgets.load();
        // Give the widget time to render
        setTimeout(() => setIsLoading(false), 1500);
    }

    // Cleanup function
    return () => {
      clearTimeout(loadingTimeout);
      if (embedRef.current) {
        embedRef.current.innerHTML = "";
      }
    };
  }, [videoUrl]); // Re-run effect if videoUrl changes

  return (
    <div style={{ position: "relative" }}>
      {isLoading && (
        <Stack gap="sm" style={{ padding: "1rem" }}>
          <Skeleton height={500} radius="md" />
          <Skeleton height={20} width="70%" />
          <Skeleton height={16} width="50%" />
        </Stack>
      )}
      <div
        ref={embedRef}
        className="tiktok-embed-container"
        style={{ display: isLoading ? "none" : "block" }}
      >
        {/* TikTok embed will be rendered here */}
      </div>
    </div>
  );
};

export default TikTokEmbed;
